import type { Message } from '@chatbot/shared'
import { PersonOutline, SmartToyOutlined } from '@mui/icons-material'
import { Avatar, Box, Paper, Typography } from '@mui/material'
import type React from 'react'
import { useAuth } from '../contexts/AuthContext'
import { ProfileAvatar } from './ProfileAvatar'

interface ChatMessageProps {
  message: Message
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const { user } = useAuth()
  const isUser = message.role === 'user'

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: { xs: 1.5, sm: 2 },
        px: { xs: 0, sm: 0 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isUser ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          maxWidth: { xs: '95%', sm: '80%' },
          width: '100%',
        }}
      >
        {isUser ? (
          <ProfileAvatar
            src={user?.picture}
            alt={user?.name}
            fallback={<PersonOutline />}
            sx={{
              bgcolor: 'primary.main',
              width: { xs: 28, sm: 32 },
              height: { xs: 28, sm: 32 },
              mx: { xs: 0.5, sm: 1 },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1rem', sm: '1.25rem' },
              },
            }}
          />
        ) : (
          <Avatar
            sx={{
              bgcolor: 'secondary.main',
              width: { xs: 28, sm: 32 },
              height: { xs: 28, sm: 32 },
              mx: { xs: 0.5, sm: 1 },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1rem', sm: '1.25rem' },
              },
            }}
          >
            <SmartToyOutlined />
          </Avatar>
        )}

        <Paper
          elevation={1}
          sx={{
            p: { xs: 1.5, sm: 2 },
            bgcolor: isUser ? 'primary.main' : 'grey.100',
            color: isUser ? 'primary.contrastText' : 'text.primary',
            borderRadius: 2,
            maxWidth: '100%',
            minWidth: 0, // Allow shrinking
            flex: 1,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              lineHeight: { xs: 1.4, sm: 1.5 },
            }}
          >
            {message.content}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              opacity: 0.7,
              display: 'block',
              mt: { xs: 0.5, sm: 1 },
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
            }}
          >
            {new Date(message.timestamp).toLocaleTimeString()}
          </Typography>
        </Paper>
      </Box>
    </Box>
  )
}
